<script lang="ts" setup generic="T extends string">
  import type { InputTypeHTMLAttribute } from 'vue'

  type Props = {
    inputProps: {
      type?: InputTypeHTMLAttribute
      disabled?: boolean
      resetable?: boolean
      readonly?: boolean
      required?: boolean
      autofocus?: boolean
      placeholder?: string
      label?: string
      name: string
      rules?: string
      icon?: string
      errors?: string[]
    }

    dropdownDisplayed?: boolean
    name: string
    full?: boolean
  }

  const props = withDefaults(defineProps<Props>(), {
    dropdownDisplayed: false,
  })

  const emit = defineEmits<{
    (e: 'reset' | 'click-outside' | 'inputClick' | 'blur'): void
  }>()

  const model = defineModel<T>({ required: true })

  const inputValue = ref(model.value)

  watch(model, (val: T) => {
    inputValue.value = val
  }, { immediate: true })

  function clickOutsideClick(): void {
    emit('click-outside')
  }

  function input(v: T) {
    model.value = v
  }

  function click() {
    emit('inputClick')
  }

  function blur() {
    if (!inputValue.value) {
      inputValue.value = model.value
    }
    emit('blur')
  }

</script>
<template>
  <div
    v-click-outside="clickOutsideClick"
    class="c-input-dropdown"
  >
    <InputBase
      :model-value="inputValue"
      v-bind="props.inputProps"
      @reset="() => emit('reset')"
      @update:model-value="(v) => input(v as T)"
      @click="click"
      @blur="blur"
    />
    <div
      class="container"
      :class="{full: props.full}"
    >
      <div
        v-if="dropdownDisplayed"
        class="dropdown"
      >
        <div
          class="options-container"
          :class="{ 'is-loading': false }"
        >
          <div class="scroll-container">
            <slot />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<style scoped>

.c-input-dropdown {
  position: relative;
  z-index: 10;

  .container{
    position: absolute;
    top: 44px;
    left: 0;
    display: flex;

    &.full{
      width: 100%;
    }

    .dropdown {
      min-width: 100%;
      max-height: 350px;
      border: solid 1px rgb(26 44 54 / 20%);
      border-radius: 0 0 8px 8px;
      border-top: none;
      background: white;
      overflow-y: auto;
      scrollbar-color: rgb(0 0 0 / 25%) transparent;

      &::-webkit-scrollbar {
        width: 8px;
        height: 8px;
        background-color: transparent;
      }

      &::-webkit-scrollbar-thumb {
        border-radius: 4px;
        background: rgb(0 0 0 / 25%);
      }
    }

  }
}
</style>
